// src/services/firebase.ts - 오류 수정된 버전

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  getDoc,
  setDoc,
  arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { 
  Patient, Surgery, Question, Attachment, Doctor, PatientSession, Institution, 
  PatientSessionLog, PatientActionItem 
} from '../types';
import { generateDoctorNumber } from '../utils/numberGenerator';

// 🔥 Firebase 연결 상태 확인 함수
export const checkFirebaseConnection = async (): Promise<boolean> => {
  try {
    console.log('🔥 Firebase 연결 상태 확인 중...');
    const testCollection = collection(db, 'test');
    await getDocs(testCollection);
    console.log('✅ Firebase 연결 성공');
    return true;
  } catch (error) {
    console.error('❌ Firebase 연결 실패:', error);
    return false;
  }
};

// ========== 개선된 세션별 로그 관리 함수들 ==========

/**
 * 세션 로그 문서 생성 (세션 시작 시)
 */
export const createSessionLog = async (
  patientName: string,
  institutionNumber: string,
  surgeryName: string,
  sessionId: string,
  doctorId?: string
): Promise<string> => {
  try {
    console.log('🔥 세션 로그 생성:', { patientName, institutionNumber, surgeryName, sessionId });
    
    // Firestore는 undefined 값을 거부하므로 doctorId가 없으면 'general'로 대체
    const sessionLogData: Omit<PatientSessionLog, 'id'> = {
      patientName,
      institutionNumber,
      surgeryName,
      doctorId: doctorId ?? 'general',
      sessionId,
      sessionStartTime: new Date(),
      actionLogs: [],
      totalActions: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'patientSessionLogs'), sessionLogData);
    console.log('✅ 세션 로그 생성 성공:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ 세션 로그 생성 오류:', error);
    throw error;
  }
};

/**
 * 세션에 행동 로그 추가 (실시간으로 누적)
 */
export const addActionToSession = async (
  sessionId: string,
  actionType: PatientActionItem['actionType'],
  actionTarget?: string,
  actionData?: any
): Promise<void> => {
  try {
    console.log('🔥 세션에 행동 추가:', { sessionId, actionType, actionTarget });
    
    // 🔥 undefined 값 필터링 및 정리
    const cleanActionData = actionData ? JSON.parse(JSON.stringify(actionData)) : null;
    
    // 세션 ID로 문서 찾기
    const q = query(
      collection(db, 'patientSessionLogs'),
      where('sessionId', '==', sessionId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.error('❌ 세션 로그 문서를 찾을 수 없음:', sessionId);
      return;
    }
    
    const sessionLogDoc = querySnapshot.docs[0];
    const sessionLogRef = doc(db, 'patientSessionLogs', sessionLogDoc.id);
    const currentData = sessionLogDoc.data();
    
    // 🔥 undefined 값을 null로 치환하여 Firestore 호환성 확보
    const newAction: Record<string, any> = {
      actionType: actionType || 'unknown',
      timestamp: new Date()
    };
    
    // actionTarget이 있을 때만 추가
    if (actionTarget && actionTarget.trim()) {
      newAction.actionTarget = actionTarget.trim();
    }
    
    // actionData가 있을 때만 추가
    if (cleanActionData && Object.keys(cleanActionData).length > 0) {
      newAction.actionData = cleanActionData;
    }
    
    console.log('📝 정리된 액션 데이터:', newAction);
    
    // 기존 로그에 새 행동 추가
    await updateDoc(sessionLogRef, {
      actionLogs: arrayUnion(newAction),
      totalActions: (currentData.totalActions || 0) + 1,
      updatedAt: new Date()
    });
    
    console.log('✅ 세션에 행동 추가 성공');
  } catch (error) {
    console.error('❌ 세션에 행동 추가 오류:', error);
    console.error('❌ 오류 상세:', {
      sessionId,
      actionType,
      actionTarget,
      actionData,
      error: error instanceof Error ? error.message : error
    });
    // 에러를 throw하지 않고 로그만 남겨서 앱이 중단되지 않도록 함
  }
};

/**
 * 세션 종료 처리 (세션 종료 시간 및 지속 시간 계산)
 */
export const endSessionLog = async (sessionId: string): Promise<void> => {
  try {
    console.log('🔥 세션 종료 처리:', sessionId);
    
    const q = query(
      collection(db, 'patientSessionLogs'),
      where('sessionId', '==', sessionId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.error('❌ 세션 로그 문서를 찾을 수 없음:', sessionId);
      return;
    }
    
    const sessionLogDoc = querySnapshot.docs[0];
    const sessionData = sessionLogDoc.data();
    const sessionLogRef = doc(db, 'patientSessionLogs', sessionLogDoc.id);
    
    const endTime = new Date();
    const startTime = sessionData.sessionStartTime.toDate();
    const duration = endTime.getTime() - startTime.getTime();
    
    // 🔥 세션 종료 행동을 undefined 값 없이 생성
    const endAction: Record<string, any> = {
      actionType: 'page_exit',
      actionTarget: 'patient_assistant_page',
      actionData: {
        sessionDuration: duration,
        totalActions: sessionData.totalActions || 0
      },
      timestamp: endTime
    };
    
    console.log('📝 세션 종료 액션 데이터:', endAction);
    
    await updateDoc(sessionLogRef, {
      actionLogs: arrayUnion(endAction),
      sessionEndTime: endTime,
      sessionDuration: duration,
      totalActions: (sessionData.totalActions || 0) + 1,
      updatedAt: endTime
    });
    
    console.log('✅ 세션 종료 처리 성공:', { duration: `${duration}ms` });
  } catch (error) {
    console.error('❌ 세션 종료 처리 오류:', error);
    console.error('❌ 세션 종료 오류 상세:', {
      sessionId,
      error: error instanceof Error ? error.message : error
    });
    // 에러를 throw하지 않고 로그만 남겨서 앱이 중단되지 않도록 함
  }
};

/**
 * 환자의 모든 세션 로그 조회
 */
export const getPatientSessionLogs = async (
  patientName: string,
  institutionNumber: string,
  surgeryName?: string
): Promise<PatientSessionLog[]> => {
  try {
    console.log('🔥 환자 세션 로그 조회:', { patientName, institutionNumber, surgeryName });
    
    let q = query(
      collection(db, 'patientSessionLogs'),
      where('patientName', '==', patientName),
      where('institutionNumber', '==', institutionNumber)
    );
    
    if (surgeryName) {
      q = query(q, where('surgeryName', '==', surgeryName));
    }
    
    const querySnapshot = await getDocs(q);
    const sessionLogs = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      sessionStartTime: doc.data().sessionStartTime?.toDate(),
      sessionEndTime: doc.data().sessionEndTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
      actionLogs: doc.data().actionLogs?.map((log: any) => ({
        ...log,
        timestamp: log.timestamp?.toDate()
      })) || []
    } as PatientSessionLog));
    
    // 최신순 정렬
    sessionLogs.sort((a, b) => b.sessionStartTime.getTime() - a.sessionStartTime.getTime());
    
    console.log('✅ 환자 세션 로그 조회 성공:', sessionLogs.length, '개 세션');
    return sessionLogs;
  } catch (error) {
    console.error('❌ 환자 세션 로그 조회 오류:', error);
    throw error;
  }
};

/**
 * 특정 세션의 상세 로그 조회
 */
export const getSessionLogDetail = async (sessionId: string): Promise<PatientSessionLog | null> => {
  try {
    console.log('🔥 세션 상세 로그 조회:', sessionId);
    
    const q = query(
      collection(db, 'patientSessionLogs'),
      where('sessionId', '==', sessionId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('⚠️ 세션 로그를 찾을 수 없음:', sessionId);
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const sessionLog = {
      id: doc.id,
      ...doc.data(),
      sessionStartTime: doc.data().sessionStartTime?.toDate(),
      sessionEndTime: doc.data().sessionEndTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
      actionLogs: doc.data().actionLogs?.map((log: any) => ({
        ...log,
        timestamp: log.timestamp?.toDate()
      })) || []
    } as PatientSessionLog;
    
    console.log('✅ 세션 상세 로그 조회 성공:', {
      sessionId,
      totalActions: sessionLog.totalActions,
      duration: sessionLog.sessionDuration
    });
    
    return sessionLog;
  } catch (error) {
    console.error('❌ 세션 상세 로그 조회 오류:', error);
    throw error;
  }
};

/**
 * 세션 로그 통계 조회 (대시보드용)
 */
export const getSessionLogStats = async (
  institutionNumber: string,
  surgeryName?: string,
  startDate?: Date,
  endDate?: Date
): Promise<{
  totalSessions: number;
  totalActions: number;
  averageSessionDuration: number;
  averageActionsPerSession: number;
  mostActivePatients: { patientName: string; sessionCount: number; actionCount: number }[];
}> => {
  try {
    console.log('🔥 세션 로그 통계 조회:', { institutionNumber, surgeryName });
    
    let q = query(
      collection(db, 'patientSessionLogs'),
      where('institutionNumber', '==', institutionNumber)
    );
    
    if (surgeryName) {
      q = query(q, where('surgeryName', '==', surgeryName));
    }
    
    const querySnapshot = await getDocs(q);
    const sessionLogs = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        patientName: data.patientName,
        sessionStartTime: data.sessionStartTime?.toDate(),
        sessionEndTime: data.sessionEndTime?.toDate(),
        totalActions: data.totalActions || 0,
        sessionDuration: data.sessionDuration || 0
      };
    });
    
    // 날짜 필터링
    let filteredLogs = sessionLogs;
    if (startDate || endDate) {
      filteredLogs = sessionLogs.filter(log => {
        const sessionDate = log.sessionStartTime;
        if (!sessionDate) return false;
        
        if (startDate && sessionDate < startDate) return false;
        if (endDate && sessionDate > endDate) return false;
        
        return true;
      });
    }
    
    const totalSessions = filteredLogs.length;
    const totalActions = filteredLogs.reduce((sum, log) => sum + (log.totalActions || 0), 0);
    const totalDuration = filteredLogs.reduce((sum, log) => sum + (log.sessionDuration || 0), 0);
    
    const averageSessionDuration = totalSessions > 0 ? totalDuration / totalSessions : 0;
    const averageActionsPerSession = totalSessions > 0 ? totalActions / totalSessions : 0;
    
    // 환자별 활동량 집계
    const patientStats = new Map<string, { sessionCount: number; actionCount: number }>();
    filteredLogs.forEach(log => {
      const existing = patientStats.get(log.patientName) || { sessionCount: 0, actionCount: 0 };
      patientStats.set(log.patientName, {
        sessionCount: existing.sessionCount + 1,
        actionCount: existing.actionCount + (log.totalActions || 0)
      });
    });
    
    const mostActivePatients = Array.from(patientStats.entries())
      .map(([patientName, stats]) => ({ patientName, ...stats }))
      .sort((a, b) => b.actionCount - a.actionCount)
      .slice(0, 10);
    
    const stats = {
      totalSessions,
      totalActions,
      averageSessionDuration,
      averageActionsPerSession,
      mostActivePatients
    };
    
    console.log('✅ 세션 로그 통계 조회 성공:', stats);
    return stats;
  } catch (error) {
    console.error('❌ 세션 로그 통계 조회 오류:', error);
    throw error;
  }
};

// ========== 기관 관리 함수들 ==========
export const addInstitution = async (institution: Omit<Institution, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    console.log('🔥 기관 추가 시작:', institution);
    
    const institutionNumber = institution.institutionNumber;
    
    const institutionData: Omit<Institution, 'id'> = {
      ...institution,
      institutionNumber,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'institutions'), institutionData);
    console.log('✅ 기관 추가 성공:', docRef.id, '기관번호:', institutionNumber);
    return { id: docRef.id, institutionNumber };
  } catch (error) {
    console.error('❌ 기관 추가 오류:', error);
    throw error;
  }
};

// ========== 수술 관련 함수들 ==========
export const addSurgery = async (surgery: Omit<Surgery, 'id'>) => {
  try {
    console.log('🔥 수술 추가 시작:', surgery);
    const docRef = await addDoc(collection(db, 'surgeries'), {
      ...surgery,
      createdAt: new Date()
    });
    
    console.log('✅ 수술 추가 성공:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ 수술 추가 오류:', error);
    throw error;
  }
};

export const updateSurgery = async (surgeryId: string, updates: Partial<Surgery>) => {
  try {
    console.log('🔥 수술 수정 시작:', surgeryId, updates);
    const surgeryRef = doc(db, 'surgeries', surgeryId);
    await updateDoc(surgeryRef, {
      ...updates,
      updatedAt: new Date()
    });
    console.log('✅ 수술 수정 성공');
  } catch (error) {
    console.error('❌ 수술 수정 오류:', error);
    throw error;
  }
};

export const getSurgeriesByInstitution = async (institutionNumber: string): Promise<Surgery[]> => {
  try {
    console.log('🔥 기관별 수술 목록 조회 시작:', institutionNumber);
    const q = query(
      collection(db, 'surgeries'),
      where('institutionNumber', '==', institutionNumber)
    );
    const querySnapshot = await getDocs(q);
    const surgeries = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Surgery));
    
    surgeries.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    console.log('✅ 기관별 수술 목록 조회 성공:', surgeries.length, '개');
    return surgeries;
  } catch (error) {
    console.error('❌ 기관별 수술 목록 조회 오류:', error);
    throw error;
  }
};

export const getSurgeries = async (): Promise<Surgery[]> => {
  try {
    console.log('🔥 전체 수술 목록 조회 시작');
    const querySnapshot = await getDocs(collection(db, 'surgeries'));
    const surgeries = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Surgery));
    
    surgeries.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    console.log('✅ 전체 수술 목록 조회 성공:', surgeries.length, '개');
    return surgeries;
  } catch (error) {
    console.error('❌ 전체 수술 목록 조회 오류:', error);
    throw error;
  }
};

export const getSurgeryByName = async (name: string): Promise<Surgery | null> => {
  try {
    console.log('🔥 수술 조회 시작:', name);
    const q = query(collection(db, 'surgeries'), where('name', '==', name));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const surgery = {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Surgery;
      console.log('✅ 수술 조회 성공:', surgery);
      return surgery;
    }
    console.log('⚠️ 수술을 찾을 수 없음:', name);
    return null;
  } catch (error) {
    console.error('❌ 수술 조회 오류:', error);
    throw error;
  }
};

// ========== 질문/답변 관련 함수들 ==========
export const addQuestion = async (question: Omit<Question, 'id'>): Promise<string> => {
  try {
    console.log('🔥 addQuestion 호출됨');
    console.log('📋 Question 데이터 상세:', {
      surgeryName: question.surgeryName || 'EMPTY',
      patientName: question.patientName || 'EMPTY',
      institutionNumber: question.institutionNumber || 'EMPTY',
      doctorId: question.doctorId || 'EMPTY',
      questionsCount: question.questions?.length || 0,
      answersCount: question.answers?.length || 0,
      createdAt: question.createdAt || 'EMPTY'
    });

    // 필수 필드 검증
    if (!question.surgeryName?.trim()) {
      throw new Error('surgeryName이 필요합니다.');
    }
    if (!question.patientName?.trim()) {
      throw new Error('patientName이 필요합니다.');
    }
    if (!question.institutionNumber?.trim()) {
      throw new Error('institutionNumber가 필요합니다.');
    }
    if (!question.questions || !Array.isArray(question.questions) || question.questions.length === 0) {
      throw new Error('questions 배열이 필요합니다.');
    }
    if (!question.answers || !Array.isArray(question.answers) || question.answers.length === 0) {
      throw new Error('answers 배열이 필요합니다.');
    }
    if (question.questions.length !== question.answers.length) {
      throw new Error(`질문 수(${question.questions.length})와 답변 수(${question.answers.length})가 일치하지 않습니다.`);
    }

    console.log('✅ 필수 필드 검증 통과');

    // 🔥 수정: 질문과 답변을 쌍으로 필터링하여 인덱스 불일치 방지
    const validPairs = question.questions
      .map((q, index) => ({ q: q.trim(), a: question.answers[index]?.trim() || '' }))
      .filter(pair => pair.q.length > 0 && pair.a.length > 0);

    if (validPairs.length === 0) {
      throw new Error('유효한 질문/답변 쌍이 없습니다.');
    }

    const dataToSave = {
      surgeryName: question.surgeryName.trim(),
      patientName: question.patientName.trim(),
      institutionNumber: question.institutionNumber.trim(),
      ...(question.doctorId?.trim() && { doctorId: question.doctorId.trim() }),
      questions: validPairs.map(p => p.q),
      answers: validPairs.map(p => p.a),
      createdAt: question.createdAt || new Date()
    };

    const docRef = await addDoc(collection(db, 'questions'), dataToSave);
    console.log('✅ Firestore addDoc 성공, 문서 ID:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('❌ addQuestion 전체 오류:', error);
    throw error;
  }
};

export const updateQuestion = async (questionId: string, updates: Partial<Question>): Promise<void> => {
  try {
    console.log('🔥 updateQuestion 호출됨');
    console.log('📋 업데이트 데이터:', {
      questionId: questionId || 'EMPTY',
      questionsCount: updates.questions?.length || 0,
      answersCount: updates.answers?.length || 0,
      questionsPreview: updates.questions?.slice(0, 2) || [],
      answersPreview: updates.answers?.slice(0, 2)?.map(a => a?.substring(0, 50) + '...') || []
    });

    if (!questionId?.trim()) {
      throw new Error('questionId가 필요합니다.');
    }

    // 🔥 추가: 업데이트 데이터 유효성 검사
    if (updates.questions && updates.answers) {
      if (updates.questions.length !== updates.answers.length) {
        throw new Error(`업데이트할 질문 수(${updates.questions.length})와 답변 수(${updates.answers.length})가 일치하지 않습니다.`);
      }
    }

    const isConnected = await checkFirebaseConnection();
    if (!isConnected) {
      throw new Error('Firebase 연결이 끊어졌습니다.');
    }
    console.log('✅ Firebase 연결 확인됨');

    const questionRef = doc(db, 'questions', questionId);
    
    console.log('🔍 기존 문서 존재 여부 확인');
    const docSnapshot = await getDoc(questionRef);
    
    if (!docSnapshot.exists()) {
      console.log('⚠️ 문서가 존재하지 않음, 새로 생성 시도');
      
      const dataToCreate = {
        ...updates,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      console.log('📝 새 문서 생성 데이터:', {
        questionsCount: dataToCreate.questions?.length || 0,
        answersCount: dataToCreate.answers?.length || 0
      });
      
      await setDoc(questionRef, dataToCreate);
      console.log('✅ 새 문서 생성 완료');
    } else {
      console.log('✅ 기존 문서 존재, 업데이트 진행');
      
      const updateData = {
        ...updates,
        updatedAt: new Date()
      };
      
      console.log('🔄 업데이트 데이터 준비:', {
        questionsCount: updateData.questions?.length || 0,
        answersCount: updateData.answers?.length || 0
      });
      
      await updateDoc(questionRef, updateData);
      console.log('✅ 문서 업데이트 완료');
    }
    
    // 업데이트 검증
    try {
      console.log('🔍 업데이트 검증 시작');
      const updatedDoc = await getDoc(questionRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        console.log('✅ 업데이트 검증 성공:', {
          id: updatedDoc.id,
          questionsCount: updatedData.questions?.length || 0,
          answersCount: updatedData.answers?.length || 0,
          updatedAt: updatedData.updatedAt
        });
      } else {
        console.error('❌ 업데이트 검증 실패: 문서가 존재하지 않음');
      }
    } catch (verifyError) {
      console.error('❌ 업데이트 검증 오류:', verifyError);
    }
    
  } catch (error) {
    console.error('❌ updateQuestion 전체 오류:', error);
    console.error('❌ 오류 상세:', {
      message: (error as Error).message,
      code: (error as any).code,
      stack: (error as Error).stack?.split('\n').slice(0, 5)
    });
    throw error;
  }
};

export const getQuestionsByPatientAndDoctor = async (
  surgeryName: string, 
  institutionNumber: string,
  patientName: string, 
  doctorId?: string
): Promise<Question | null> => {
  try {
    console.log('🔥 QA 조회 시작:', { 
      surgeryName, 
      institutionNumber,
      patientName, 
      doctorId: doctorId || '일반' 
    });
    
    const basicQuery = query(
      collection(db, 'questions'),
      where('surgeryName', '==', surgeryName),
      where('institutionNumber', '==', institutionNumber),
      where('patientName', '==', patientName)
    );
    
    const querySnapshot = await getDocs(basicQuery);
    console.log('🔍 기본 쿼리 결과:', querySnapshot.docs.length, '개 문서 발견');
    
    if (querySnapshot.empty) {
      console.log('📝 상담 기록이 없음 - 새로운 환자-수술 조합');
      return null;
    }

    const allDocs = querySnapshot.docs;
    let targetDoc = null;
    
    if (doctorId) {
      targetDoc = allDocs.find(doc => {
        const data = doc.data();
        return data.doctorId === doctorId;
      });
    }
    
    if (!targetDoc) {
      targetDoc = allDocs.find(doc => {
        const data = doc.data();
        return !data.doctorId || data.doctorId === 'general' || data.doctorId === '';
      });
    }
    
    if (!targetDoc && allDocs.length > 0) {
      targetDoc = allDocs[0];
    }
    
    if (targetDoc) {
      const data = targetDoc.data();
      const question: Question = {
        id: targetDoc.id,
        surgeryName: data.surgeryName || surgeryName,
        patientName: data.patientName || patientName,
        institutionNumber: data.institutionNumber || institutionNumber,
        doctorId: data.doctorId,
        questions: Array.isArray(data.questions) ? data.questions : [],
        answers: Array.isArray(data.answers) ? data.answers : [],
        createdAt: data.createdAt?.toDate() || new Date()
      };
      
      console.log('✅ QA 조회 최종 성공:', {
        id: question.id,
        questionsCount: question.questions.length,
        answersCount: question.answers.length,
        doctorId: question.doctorId || '일반'
      });
      
      return question;
    }
    
    return null;
    
  } catch (error) {
    console.error('❌ QA 조회 전체 오류:', error);
    return null;
  }
};

// ========== 환자 세션 관리 함수들 ==========
export const addPatientSession = async (session: Omit<PatientSession, 'id'>) => {
  try {
    console.log('🔥 환자 세션 추가 시작:', {
      patientName: session.patientName,
      surgeryName: session.surgeryName,
      doctorId: session.doctorId,
      hasSpecialConditions: !!session.specialConditions,
      hasCustomizedSections: !!session.customizedSections,
      customizedSectionsCount: session.customizedSections?.length || 0
    });

    const sessionData = {
      ...session,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'patientSessions'), sessionData);
    console.log('✅ 환자 세션 추가 성공:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('❌ 환자 세션 추가 오류:', error);
    throw error;
  }
};

export const getPatientSessions = async (institutionNumber: string, patientName: string): Promise<PatientSession[]> => {
  try {
    console.log('🔥 환자 세션 목록 조회 시작:', { institutionNumber, patientName });
    const q = query(
      collection(db, 'patientSessions'),
      where('institutionNumber', '==', institutionNumber),
      where('patientName', '==', patientName)
    );
    const querySnapshot = await getDocs(q);
    const sessions = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        customizedSections: data.customizedSections || null
      } as PatientSession;
    });
    
    sessions.sort((a, b) => {
      const dateA = a.updatedAt || a.createdAt || new Date(0);
      const dateB = b.updatedAt || b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    console.log('✅ 환자 세션 목록 조회 성공:', sessions.length, '개');
    return sessions;
  } catch (error) {
    console.error('❌ 환자 세션 목록 조회 오류:', error);
    throw error;
  }
};

export const updatePatientSession = async (sessionId: string, updates: Partial<PatientSession>): Promise<void> => {
  try {
    console.log('🔥 환자 세션 업데이트 시작:', sessionId);

    const sessionRef = doc(db, 'patientSessions', sessionId);
    await updateDoc(sessionRef, {
      ...updates,
      updatedAt: new Date()
    });
    
    console.log('✅ 환자 세션 업데이트 성공');
  } catch (error) {
    console.error('❌ 환자 세션 업데이트 오류:', error);
    throw error;
  }
};

// ========== 환자 관련 함수들 ==========
export const addPatient = async (patient: Omit<Patient, 'id'>) => {
  try {
    console.log('🔥 환자 추가 시작:', patient);
    
    const patientData: Omit<Patient, 'id'> = {
      ...patient
    };
    
    const docRef = await addDoc(collection(db, 'patients'), patientData);
    console.log('✅ 환자 추가 성공:', docRef.id);
    return { id: docRef.id };
  } catch (error) {
    console.error('❌ 환자 추가 오류:', error);
    throw error;
  }
};

export const authenticatePatient = async (
  institutionNumber: string,
  name: string
): Promise<Patient | null> => {
  try {
    console.log('🔥 환자 인증 시작:', { institutionNumber, name });
    
    const q = query(
      collection(db, 'patients'),
      where('institutionNumber', '==', institutionNumber),
      where('name', '==', name)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const patient = { id: doc.id, ...doc.data() } as Patient;
      console.log('✅ 환자 인증 성공:', patient);
      return patient;
    }

    console.log('⚠️ 환자 인증 실패: 일치하는 정보 없음');
    return null;

  } catch (error) {
    console.error('❌ 환자 인증 오류:', error);
    throw error;
  }
};

export const getPatientByName = async (institutionNumber: string, patientName: string): Promise<Patient | null> => {
  try {
    console.log('🔥 환자 조회 시작:', { institutionNumber, patientName });
    const q = query(
      collection(db, 'patients'), 
      where('institutionNumber', '==', institutionNumber),
      where('name', '==', patientName)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const patient = { id: doc.id, ...doc.data() } as Patient;
      console.log('✅ 환자 조회 성공:', patient);
      return patient;
    }
    console.log('⚠️ 환자를 찾을 수 없음:', { institutionNumber, patientName });
    return null;
  } catch (error) {
    console.error('❌ 환자 조회 오류:', error);
    throw error;
  }
};

// ========== 의사 관련 함수들 ==========
export const addDoctor = async (doctor: Omit<Doctor, 'id' | 'doctorNumber'>) => {
  try {
    console.log('🔥 의사 추가 시작:', doctor);
    
    const doctorNumber = await generateDoctorNumber(doctor.institutionNumber);
    
    const doctorData: Omit<Doctor, 'id'> = {
      ...doctor,
      doctorNumber
    };
    
    const docRef = await addDoc(collection(db, 'doctors'), doctorData);
    console.log('✅ 의사 추가 성공:', docRef.id, '의사번호:', doctorNumber);
    return { id: docRef.id, doctorNumber };
  } catch (error) {
    console.error('❌ 의사 추가 오류:', error);
    throw error;
  }
};

export const getDoctorById = async (doctorId: string): Promise<Doctor | null> => {
  try {
    console.log('🔥 의사 조회 시작:', doctorId);
    const docRef = doc(db, 'doctors', doctorId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const doctor = { id: docSnap.id, ...docSnap.data() } as Doctor;
      console.log('✅ 의사 조회 성공:', doctor);
      return doctor;
    }
    console.log('⚠️ 의사를 찾을 수 없음:', doctorId);
    return null;
  } catch (error) {
    console.error('❌ 의사 조회 오류:', error);
    throw error;
  }
};

// ========== 기관 관련 함수들 (필요한 것만) ==========
export const getInstitutions = async (): Promise<Institution[]> => {
  try {
    console.log('🔥 기관 목록 조회 시작');
    const querySnapshot = await getDocs(collection(db, 'institutions'));
    const institutions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Institution));
    
    institutions.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    console.log('✅ 기관 목록 조회 성공:', institutions.length, '개');
    return institutions;
  } catch (error) {
    console.error('❌ 기관 목록 조회 오류:', error);
    throw error;
  }
};

export const validateInstitutionNumber = async (institutionNumber: string): Promise<boolean> => {
  try {
    console.log('🔥 기관번호 유효성 확인:', institutionNumber);
    const q = query(
      collection(db, 'institutions'),
      where('institutionNumber', '==', institutionNumber),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const isValid = !querySnapshot.empty;
    console.log(`✅ 기관번호 유효성 확인 결과: ${isValid}`);
    return isValid;
  } catch (error) {
    console.error('❌ 기관번호 유효성 확인 오류:', error);
    return false;
  }
};

// ========== 누락된 기관 관련 함수들 복구 ==========
export const updateInstitution = async (institutionId: string, updates: Partial<Institution>): Promise<void> => {
  try {
    console.log('🔥 기관 정보 업데이트 시작:', institutionId, updates);
    
    const institutionRef = doc(db, 'institutions', institutionId);
    await updateDoc(institutionRef, {
      ...updates,
      updatedAt: new Date()
    });
    
    console.log('✅ 기관 정보 업데이트 성공');
  } catch (error) {
    console.error('❌ 기관 정보 업데이트 오류:', error);
    throw error;
  }
};

export const getInstitutionByNumber = async (institutionNumber: string): Promise<Institution | null> => {
  try {
    console.log('🔥 기관 조회 시작:', institutionNumber);
    const q = query(
      collection(db, 'institutions'),
      where('institutionNumber', '==', institutionNumber)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const institution = {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Institution;
      console.log('✅ 기관 조회 성공:', institution);
      return institution;
    }
    
    console.log('⚠️ 기관을 찾을 수 없음:', institutionNumber);
    return null;
  } catch (error) {
    console.error('❌ 기관 조회 오류:', error);
    throw error;
  }
};

// ========== 누락된 의사 관련 함수들 복구 ==========
export const checkDoctorExists = async (institutionNumber: string, doctorNumber: string): Promise<boolean> => {
  try {
    console.log('🔥 의사 중복 확인:', { institutionNumber, doctorNumber });
    const q = query(
      collection(db, 'doctors'), 
      where('institutionNumber', '==', institutionNumber),
      where('doctorNumber', '==', doctorNumber)
    );
    const querySnapshot = await getDocs(q);
    const exists = !querySnapshot.empty;
    console.log(`✅ 의사 중복 확인 결과: ${exists}`);
    return exists;
  } catch (error) {
    console.error('❌ 의사 중복 확인 오류:', error);
    throw error;
  }
};

export const getDoctorsByInstitution = async (institutionNumber: string): Promise<Doctor[]> => {
  try {
    console.log('🔥 기관별 의사 목록 조회 시작:', institutionNumber);
    const q = query(
      collection(db, 'doctors'),
      where('institutionNumber', '==', institutionNumber)
    );
    const querySnapshot = await getDocs(q);
    const doctors = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Doctor));
    console.log('✅ 기관별 의사 목록 조회 성공:', doctors.length, '명');
    return doctors;
  } catch (error) {
    console.error('❌ 기관별 의사 목록 조회 오류:', error);
    throw error;
  }
};

export const getDoctors = async (): Promise<Doctor[]> => {
  try {
    console.log('🔥 전체 의사 목록 조회 시작');
    const querySnapshot = await getDocs(collection(db, 'doctors'));
    const doctors = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Doctor));
    console.log('✅ 전체 의사 목록 조회 성공:', doctors.length, '명');
    return doctors;
  } catch (error) {
    console.error('❌ 전체 의사 목록 조회 오류:', error);
    throw error;
  }
};

export const updateDoctor = async (doctorId: string, updates: Partial<Doctor>): Promise<void> => {
  try {
    console.log('🔥 의사 정보 업데이트 시작:', doctorId, updates);
    
    if (!doctorId) {
      throw new Error('의사 ID가 필요합니다.');
    }

    const isConnected = await checkFirebaseConnection();
    if (!isConnected) {
      throw new Error('Firebase 연결이 끊어졌습니다.');
    }

    const doctorRef = doc(db, 'doctors', doctorId);
    
    const docSnapshot = await getDoc(doctorRef);
    if (!docSnapshot.exists()) {
      throw new Error('존재하지 않는 의사입니다.');
    }

    await updateDoc(doctorRef, updates);
    console.log('✅ 의사 정보 업데이트 성공');
    
  } catch (error) {
    console.error('❌ 의사 정보 업데이트 오류:', error);
    throw error;
  }
};

// ========== 누락된 환자 관련 함수들 복구 ==========
export const checkPatientExists = async (institutionNumber: string, patientNumber: string): Promise<boolean> => {
  try {
    console.log('🔥 환자 중복 확인:', { institutionNumber, patientNumber });
    const q = query(
      collection(db, 'patients'), 
      where('institutionNumber', '==', institutionNumber),
      where('patientNumber', '==', patientNumber)
    );
    const querySnapshot = await getDocs(q);
    const exists = !querySnapshot.empty;
    console.log(`✅ 환자 중복 확인 결과: ${exists}`);
    return exists;
  } catch (error) {
    console.error('❌ 환자 중복 확인 오류:', error);
    throw error;
  }
};

export const getPatientsByInstitution = async (institutionNumber: string): Promise<Patient[]> => {
  try {
    console.log('🔥 기관별 환자 목록 조회 시작:', institutionNumber);
    const q = query(
      collection(db, 'patients'),
      where('institutionNumber', '==', institutionNumber)
    );
    const querySnapshot = await getDocs(q);
    const patients = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Patient));
    console.log('✅ 기관별 환자 목록 조회 성공:', patients.length, '명');
    return patients;
  } catch (error) {
    console.error('❌ 기관별 환자 목록 조회 오류:', error);
    throw error;
  }
};

export const getPatients = async (): Promise<Patient[]> => {
  try {
    console.log('🔥 전체 환자 목록 조회 시작');
    const querySnapshot = await getDocs(collection(db, 'patients'));
    const patients = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Patient));
    console.log('✅ 전체 환자 목록 조회 성공:', patients.length, '명');
    return patients;
  } catch (error) {
    console.error('❌ 전체 환자 목록 조회 오류:', error);
    throw error;
  }
};

export const updatePatient = async (patientId: string, updates: Partial<Patient>): Promise<void> => {
  try {
    console.log('🔥 환자 정보 업데이트 시작:', patientId, updates);
    
    if (!patientId) {
      throw new Error('환자 ID가 필요합니다.');
    }

    const isConnected = await checkFirebaseConnection();
    if (!isConnected) {
      throw new Error('Firebase 연결이 끊어졌습니다.');
    }

    const patientRef = doc(db, 'patients', patientId);
    
    const docSnapshot = await getDoc(patientRef);
    if (!docSnapshot.exists()) {
      throw new Error('존재하지 않는 환자입니다.');
    }

    await updateDoc(patientRef, updates);
    console.log('✅ 환자 정보 업데이트 성공');
    
  } catch (error) {
    console.error('❌ 환자 정보 업데이트 오류:', error);
    throw error;
  }
};

export const addPatientToSurgery = async (surgeryName: string, patientId: string): Promise<void> => {
  try {
    console.log('🔥 수술에 환자 추가:', { surgeryName, patientId });
    
    const q = query(collection(db, 'surgeries'), where('name', '==', surgeryName));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const surgeryDoc = querySnapshot.docs[0];
      const surgeryData = surgeryDoc.data() as Surgery;
      
      if (!surgeryData.patientIds.includes(patientId)) {
        const updatedPatientIds = [...surgeryData.patientIds, patientId];
        await updateDoc(doc(db, 'surgeries', surgeryDoc.id), {
          patientIds: updatedPatientIds,
          updatedAt: new Date()
        });
        console.log('✅ 수술에 환자 추가 성공');
      } else {
        console.log('⚠️ 환자가 이미 수술에 추가되어 있음');
      }
    } else {
      console.error('❌ 수술을 찾을 수 없음:', surgeryName);
    }
  } catch (error) {
    console.error('❌ 수술에 환자 추가 오류:', error);
    throw error;
  }
};

// ========== 누락된 QA 통계 함수 복구 ==========
export const getPatientQAStats = async (
  institutionNumber: string,
  patientName: string,
  surgeryName: string
): Promise<{ totalQuestions: number; totalAnswers: number; lastActivity?: Date }> => {
  try {
    console.log('🔥 환자 QA 통계 조회:', { institutionNumber, patientName, surgeryName });
    
    const q = query(
      collection(db, 'questions'),
      where('institutionNumber', '==', institutionNumber),
      where('patientName', '==', patientName),
      where('surgeryName', '==', surgeryName)
    );
    
    const querySnapshot = await getDocs(q);
    
    let totalQuestions = 0;
    let totalAnswers = 0;
    let lastActivity: Date | undefined;
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.questions) totalQuestions += data.questions.length;
      if (data.answers) totalAnswers += data.answers.length;
      
      const docDate = data.createdAt?.toDate();
      if (docDate && (!lastActivity || docDate > lastActivity)) {
        lastActivity = docDate;
      }
    });
    
    console.log('✅ QA 통계 조회 완료:', { totalQuestions, totalAnswers, lastActivity });
    
    return { totalQuestions, totalAnswers, lastActivity };
    
  } catch (error) {
    console.error('❌ QA 통계 조회 오류:', error);
    return { totalQuestions: 0, totalAnswers: 0 };
  }
};

// ========== 파일 업로드 함수 ==========
export const uploadFile = async (file: File, path: string): Promise<Attachment> => {
  try {
    if (!file) {
      throw new Error('파일이 선택되지 않았습니다.');
    }

    if (file.size > 10 * 1024 * 1024) {
      throw new Error('파일 크기가 10MB를 초과합니다.');
    }

    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      throw new Error('이미지 또는 동영상 파일만 업로드 가능합니다.');
    }

    console.log('업로드 시작:', {
      name: file.name,
      type: file.type,
      size: file.size,
      path: path
    });

    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const sanitizedName = file.name
      .replace(/[^a-zA-Z0-9.-]/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_|_$/g, '');
    
    const fileName = `${timestamp}_${randomString}_${sanitizedName}`;

    const cleanPath = path.replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
    const fileRef = ref(storage, `${cleanPath}/${fileName}`);
    
    console.log('Storage 참조 생성:', fileRef.fullPath);

    const metadata = {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        fileSize: file.size.toString()
      }
    };

    console.log('파일 업로드 시작...');

    const snapshot = await uploadBytes(fileRef, file, metadata);
    console.log('업로드 완료:', snapshot.ref.fullPath);

    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('다운로드 URL 생성:', downloadURL);

    const attachment: Attachment = {
      id: snapshot.ref.name,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      url: downloadURL,
      name: file.name
    };

    console.log('첨부파일 객체 생성 완료:', attachment);
    return attachment;

  } catch (error: any) {
    console.error('파일 업로드 상세 오류:', {
      error: error,
      message: error?.message,
      code: error?.code,
      fileName: file?.name
    });

    if (error?.code) {
      switch (error.code) {
        case 'storage/unauthorized':
          throw new Error('파일 업로드 권한이 없습니다. Firebase 설정을 확인해주세요.');
        case 'storage/canceled':
          throw new Error('파일 업로드가 취소되었습니다.');
        case 'storage/quota-exceeded':
          throw new Error('Storage 용량이 초과되었습니다.');
        case 'storage/unauthenticated':
          throw new Error('인증되지 않은 요청입니다.');
        case 'storage/retry-limit-exceeded':
          throw new Error('업로드 재시도 한도를 초과했습니다. 잠시 후 다시 시도해주세요.');
        case 'storage/invalid-argument':
          throw new Error('잘못된 인수입니다. 파일을 확인해주세요.');
        case 'storage/no-default-bucket':
          throw new Error('기본 Storage 버킷이 설정되지 않았습니다.');
        case 'storage/cannot-slice-blob':
          throw new Error('파일을 처리할 수 없습니다.');
        case 'storage/server-file-wrong-size':
          throw new Error('서버에서 파일 크기 오류가 발생했습니다.');
        default:
          throw new Error(`Storage 오류 (${error.code}): ${error.message}`);
      }
    }

    if (error?.message?.includes('ERR_SSL_PROTOCOL_ERROR')) {
      throw new Error('네트워크 연결 오류입니다. 방화벽 설정을 확인하거나 다른 네트워크에서 시도해주세요.');
    }

    if (error?.message?.includes('Failed to fetch')) {
      throw new Error('서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.');
    }

    if (error?.message?.includes('ERR_NETWORK')) {
      throw new Error('네트워크 오류입니다. 인터넷 연결을 확인해주세요.');
    }

    throw new Error(`파일 업로드 실패: ${error?.message || '알 수 없는 오류'}`);
  }
};